"use client";
import { useRef, useEffect } from "react";
import {
  axisBottom,
  axisLeft,
  forceSimulation,
  forceManyBody,
  forceX,
  forceCollide,
  forceY,
  scaleBand,
  scaleLinear,
  select,
} from "d3";
import styles from "./styles.module.css";
import useElementSize from "@/app/hooks/useElementSize";
import { COLOR_PALETTE_PROPOSAL, SHORT_MONTHS } from "@/app/utils/constants";
import { decomposeNumber } from "@/app/utils/math";

export default function BubbleChart({ data }: { data: number[][] }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(chartRef);

  useEffect(() => {
    // Check clause
    if (!width) return;
    if (!chartRef.current) return;
    if (data.length !== 12) return;

    // Chart size
    const isMobile = width < 468;
    const margin = { top: 40, right: 20, bottom: 20, left: isMobile ? 0 : 60 };
    const outerWidth = width;
    const outerHeight = 360;
    const innerWidth = outerWidth - margin.left - margin.right;
    const innerHeight = outerHeight - margin.top - margin.bottom;

    // X scale (time)
    const xScale = scaleBand()
      .range([0, innerWidth])
      .domain(SHORT_MONTHS)
      .paddingInner(0);

    // Y scale (groups)
    const yScale = scaleLinear().range([innerHeight, 0]).domain([0, 4]);

    // Radius scale for circles
    const rScale = scaleBand()
      .range([0, 16, 32, 64])
      .domain(["0", "1", "10", "100"]);

    // Render the chart
    const svg = select(svgRef.current)
      .data([null])
      .join("svg")
      .classed("svg_chart", true)
      .attr("width", outerWidth)
      .attr("height", outerHeight)
      .attr("viewBox", [0, 0, outerWidth, outerHeight]);

    // Group
    const g = svg
      .selectAll(".svg_g")
      .data([null])
      .join("g")
      .classed("svg_g", true)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X grid
    const xBandwidth = xScale.bandwidth();
    g.selectAll(".xGrid")
      .data(SHORT_MONTHS)
      .join("line")
      .classed("xGrid", true)
      .attr("x1", (d: any) => (xScale(d) || 0) + xBandwidth / 2)
      .attr("x2", (d: any) => (xScale(d) || 0) + xBandwidth / 2)
      .attr("y1", innerHeight)
      .attr("y2", 0)
      .style("stroke", "#5a728240")
      .style("stroke-width", 0.5);

    // Y grid
    g.selectAll(".yGrid")
      .data([0, 1, 2, 3, 4])
      .join("line")
      .classed("yGrid", true)
      .attr("x1", -margin.left)
      .attr("x2", innerWidth + margin.left)
      .attr("y1", (d: any) => yScale(d) || 0)
      .attr("y2", (d: any) => yScale(d) || 0)
      .style("stroke", (d: any) => (d ? "#5a728240" : "#5a7282"))
      .style("stroke-width", 0.5);

    // X axis
    axisBottom(xScale);

    // Draw x labels
    g.selectAll(".xLabel")
      .data(SHORT_MONTHS)
      .join("text")
      .classed("xLabel", true)
      .attr("x", (d: any) => (xScale(d) || 0) + xBandwidth / 2)
      .attr("y", innerHeight + margin.bottom / 2 + 6)
      .text((d: any) => d)
      .style("text-anchor", "middle")
      .style("alignment-baseline", "baseline")
      .style("font-size", 12);

    // Draw x values
    g.selectAll(".xLabel-values")
      .data(data)
      .join("text")
      .classed("xLabel-values", true)
      .attr(
        "x",
        (_, i: number) => (xScale(SHORT_MONTHS[i]) || 0) + xBandwidth / 2,
      )
      .attr("y", -8)
      .text((d: any) => d[0] + d[1])
      .style("fill", "#5a7282")
      .style("text-anchor", "middle")
      .style("alignment-baseline", "baseline")
      .style("font-size", 10)
      .style("font-weight", "600");

    // Y axis
    axisLeft(yScale);

    // Draw y labels
    if (!isMobile) {
      g.selectAll(".yLabel")
        .data([1, 3])
        .join("text")
        .classed("yLabel", true)
        .attr("x", -margin.left)
        .attr("y", (d: any) => (yScale(d) || 0) - 4)
        .text((d: any) => (d === 1 ? "Onchain" : "Offchain"))
        .style("fill", "#5a7282")
        .style("text-anchor", "start")
        .style("alignment-baseline", "baseline")
        .style("font-size", 10);
    }

    const nodes: any[] = [];
    for (let i = 0; i < data.length; i++) {
      const values = data[i];
      const category = SHORT_MONTHS[i];

      const numArr0: number[] = decomposeNumber(values[0]);
      for (let j = 0; j < numArr0.length; j++) {
        const subcategory = 1; // onchain
        const color = COLOR_PALETTE_PROPOSAL["onchain"];
        const radius = rScale(String(numArr0[j]));
        nodes.push({ radius, category, subcategory, color });
      }

      const numArr1: number[] = decomposeNumber(values[1]);
      for (let k = 0; k < numArr1.length; k++) {
        const subcategory = 3; // offchain
        const color = COLOR_PALETTE_PROPOSAL["offchain"];
        const radius = rScale(String(numArr1[k]));
        nodes.push({ radius, category, subcategory, color });
      }
    }

    // Start the simulation
    const simulation = forceSimulation(nodes)
      .force("charge", forceManyBody().strength(1))
      .force(
        "x",
        forceX().x((d: any) => (xScale(d.category) || 0) + xBandwidth / 2),
      )
      .force(
        "y",
        forceY().y((d: any) => yScale(d.subcategory) || 0),
      )
      .force(
        "collision",
        forceCollide().radius((d: any) => d.radius),
      )
      .on("tick", ticked);

    // Run the simulation until it stabilizes
    simulation.alpha(1).restart(); // Ensure forces start at full strength
    for (let i = 0; i < 300; ++i) simulation.tick(); // Advance the simulation 300 ticks
    simulation.stop(); // Stop the simulation

    // Render the final state
    ticked();

    // Tick function for rendering
    function ticked() {
      g.selectAll("circle")
        .data(nodes)
        .join("circle")
        .attr("r", (d) => d.radius)
        .attr("cx", (d) => d.x)
        .attr("cy", (d) => d.y)
        .style("fill", (d) => `${d.color}60`)
        .style("stroke", (d) => d.color);
    }

    // Create tooltip
    const tooltip = select(tooltipRef.current);

    // Overlay
    svg
      .selectAll(".g_overlay")
      .data(data)
      .join("rect")
      .classed("g_overlay", true)
      .attr("data-index", (_: any, i: number) => i)
      .attr("rx", 2)
      .attr("x", (_: any, i: number) => xScale(SHORT_MONTHS[i]) || 0)
      .attr("y", yScale(4))
      .attr("height", innerHeight - yScale(4))
      .attr("width", xScale.bandwidth())
      .attr("transform", `translate(${margin.left},${margin.top})`)
      .style("fill", "transparent")
      .on("mouseenter", function (event, d: any) {
        const i = Number(select(this).attr("data-index"));
        select(this).style("fill", "#D0D4DC20");

        // Add tooltip
        tooltip.style("display", "block").html(
          `<div>
              <div>${SHORT_MONTHS[i]}</div>
              <hr />
              <div className=${styles.test}>
                <div>${d[1]}</div>
                <div>offchain</div>
              </div>
              <div>
                <div>${d[0]}</div>
                <div>onchain</div>
              </div>
              <hr />
              <div>
                <div>${d[0] + d[1]}</div>
                <div>PROPOSALS</div>
              </div>
          </div>`,
        );

        const posX =
          event.offsetX < innerWidth * 0.8
            ? event.offsetX + 48
            : event.offsetX - 184;
        const posY = event.offsetY + 24;

        // Position tooltip
        tooltip.style("top", `${posY}px`).style("left", `${posX}px`);
      })
      .on("mousemove", function (event) {
        const posX =
          event.offsetX < innerWidth * 0.8
            ? event.offsetX + 48
            : event.offsetX - 184;
        const posY = event.offsetY + 24;
        tooltip.style("top", `${posY}px`).style("left", `${posX}px`);
      })
      .on("mouseleave", function () {
        select(this).style("fill", "transparent");

        // Remove tooltip
        tooltip
          .style("display", "none")
          .style("top", 0)
          .style("left", 0)
          .html("");
      });

    // Clean up
    return () => {
      svg.selectAll("*").remove();
    };
  }, [data, width]);

  return (
    <div className={styles.bubbleChart} ref={chartRef}>
      <div className={styles.bubbleChartLegend}>
        <div className={styles.bubbleChartLegendItem}>
          <div></div>
          <div>100 proposals</div>
        </div>
        <div className={styles.bubbleChartLegendItem}>
          <div></div>
          <div>10 proposals</div>
        </div>
        <div className={styles.bubbleChartLegendItem}>
          <div></div>
          <div>1 proposal</div>
        </div>
      </div>
      <svg ref={svgRef}></svg>
      <div className={styles.bubbleChartTooltip} ref={tooltipRef}></div>
    </div>
  );
}
